import React, { useRef, useState } from 'react'
import { connect as connectRedux } from 'react-redux'
import compose from 'recompose/compose'
import { fromJS, List, Map } from 'immutable'
import _get from 'lodash/get'
import _compact from 'lodash/compact'
import {
  createUpstreamContext,
  updateUpstreamContext,
  upstreamContextOnClick,
  CONTEXT_NAME_ADMIN_TITLE,
} from '../../services/upstream-context/index'
import {
  DISPLAY_MODE_TILE_LIST_TOP_TEN,
  DISPLAY_MODE_TILE_LIST_TOP_TEN_3X4,
  TYPE_CONTINUE_WATCHING,
  DISPLAY_MODE_DEFAULT,
  DISPLAY_MODE_COURSES,
  SECTIONS_CONFIG,
  MAX_ROW_ITEMS,
  getPmSectionTabs,
  isShifted,
  sendRowImpressionGA4Event,
} from '../../services/pm-section/index'
import { getTileDisplayType } from '../Tile.v2/utils'
import SectionRemoved from '../SectionRemoved/index'
import { getBoundActions } from '../../actions/index'
import { EN } from '../../services/languages/constants'
import { getUserLanguagePrimary } from '../../services/user/index'
import TileV2GraphqlManager from '../Tile.v2GraphqlManager/index'
import { createTitleGroupId } from '../../services/content/index'
import { get as getConfig } from '../../config/index'
import { VIEW_MORE_CLICKED_GA4_EVENT } from '../../services/event-tracking/index'
import { WINBACK_ROW, isWinbackFlagEnabled } from '../../services/winback/index'
import SectionTilesListHeader from './SectionTilesListHeader'
import { getRowStyle, getViewAll } from './utils'

const config = getConfig()
const tilesV2GraphqlActive = !!_get(config, ['features', 'tilesV2Graphql'])

function SectionTilesList (props) {
  const {
    section,
    sectionId,
    sectionType,
    sectionIndex,
    headerLabel,
    footerLabel,
    screenType,
    listType,
    displayMode,
    tabs,
    defaultTab,
    language,
    adminTitle,
    removed,
    staticText,
    addBackHandler,
    prevSectionType,
    setEventDefault,
    setUpstreamContext,
    viewAllLink,
    uiLocation,
    featureTracking,
    isWinbackRow,
    isWinbackFlowActive,
    rowPlacement,
  } = props

  const sectionConfig = SECTIONS_CONFIG[sectionType] || {}
  const viewAllDefault = getViewAll({ viewAllLink, sectionConfig, staticText })

  const [activeTabIndex, setActiveTabIndex] = useState(0)
  const [viewAll, setViewAll] = useState(viewAllDefault)

  // The following values should not trigger component re-rendering
  // so they are defined as refs
  const isSectionVisible = useRef(false)
  const visibleIndexes = useRef([])
  const sentIndexes = useRef([])
  const sectionItems = useRef(List())
  const upstreamContext = useRef(createUpstreamContext({
    screenType,
    contextType: sectionConfig.contextType,
    sectionId,
    sectionIndex,
    headerLabel: sectionConfig.contextLabel || headerLabel,
  }))
  const infoArray = visibleIndexes.current
    .map((itemIndex) => {
      const item = sectionItems.current.get(itemIndex)
      return item ? `${item.get('contentId')} | ${item.get('title')}` : null
    })
    .filter((item) => item !== null)
    .join(' | ')

  const rowStyle = getRowStyle(displayMode)

  const checkAndSendImpressionGAEvent = () => {
    sentIndexes.current[activeTabIndex] = sentIndexes.current[activeTabIndex] || []

    if (isSectionVisible.current && sectionItems.current.size > 0) {
      const unsetIndexes = visibleIndexes.current
        .filter((index) => !sentIndexes.current[activeTabIndex].includes(index))
      const rowType = sectionType
      const visibilityRules = section.getIn(['data', 'visibility'])

      if (unsetIndexes.length) {
        sendRowImpressionGA4Event({
          sectionIndex,
          rowPlacement,
          rowType,
          visibilityRules,
          setEventDefault,
          unsetIndexes,
          items: sectionItems.current.size > 0 ? sectionItems.current : List(),
          adminTitle,
          language,
          visibleIndexes: visibleIndexes.current,
          tilesV2GraphqlActive,
          ...(uiLocation && { uiLocation }),
        })
        sentIndexes.current[activeTabIndex].push(...unsetIndexes)
      }
    }
  }

  const onRenderContent = (indexes) => {
    visibleIndexes.current = indexes
    checkAndSendImpressionGAEvent()
  }

  const onVisibilityChange = (isVisible, itemsData) => {
    // if we don't have a contentId, then it means the data isn't loaded yet
    if (!itemsData.getIn([0, 'contentId']) || !isVisible) {
      return
    }

    isSectionVisible.current = isVisible
    sectionItems.current = itemsData || List()
    checkAndSendImpressionGAEvent()
  }

  const handleTabChange = (e, index) => {
    e.preventDefault()
    const tab = tabs.get(index)
    if (tab) {
      const filter = tab.getIn(['event', 'filter'])
      if (filter) {
        const filtersContext = upstreamContext.current.set('filters', { watched: filter })
        upstreamContext.current = filtersContext
      }
      if (sectionConfig.viewAll && sectionConfig.viewAll[index]) {
        setViewAll(
          fromJS({
            ...sectionConfig.viewAll[index],
            label: staticText.get(sectionConfig.viewAll[index].linkTextKey),
          }),
        )
      }
      setActiveTabIndex(index)
    }
  }

  const handleViewAllClick = (e) => {
    if (viewAll && viewAll.contextType) {
      const updatedContext = upstreamContext.current.set('contextType', viewAll.contextType)
      upstreamContextOnClick(null, { upstreamContext: updatedContext, setUpstreamContext })
    }

    // fire event
    setEventDefault(VIEW_MORE_CLICKED_GA4_EVENT.mergeDeep({
      row_number: sectionIndex,
      row_type: section.getIn(['data', 'type']),
      visible_tiles: visibleIndexes.current || undefined,
      visibility_rules: section.getIn(['data', 'visibility']),
      content_info_array: infoArray,
      row_placement: rowPlacement,
      row_name: adminTitle,
      ui_location: 'Member Home',
      page_path: e.target.pathname || null,
    }))
  }

  const sectionTitle = sectionConfig.titleTextKey
    ? staticText.get(sectionConfig.titleTextKey)
    : headerLabel

  const shifted = isShifted(prevSectionType, displayMode, featureTracking)

  const sectionClassName = [
    'section-tiles-list',
    listType ? `section-tiles-list--${listType}` : null,
    shifted ? 'section-tiles-list--shift' : null,
    shifted && tabs.size > 1 ? 'section-tiles-list--title-hidden' : null,
    displayMode ? `section-tiles-list--${displayMode}` : `section-tiles-list--${DISPLAY_MODE_DEFAULT}`,
    isWinbackRow ? 'section-tiles-list--winback' : null,
  ].filter((cls) => cls).join(' ')

  const rowClassName = [
    'section-tiles-list__row',
    listType ? `section-tiles-list__row--${listType}` : null,
    sectionType === TYPE_CONTINUE_WATCHING ? 'test-continue-watching' : null,
  ].filter((cls) => cls).join(' ')

  const tileDisplay = getTileDisplayType(displayMode)
  const activeTab = tabs.get(activeTabIndex)

  let items = activeTab ? activeTab.get('tabContent', List()) : List()
  items = items.slice(0, MAX_ROW_ITEMS)

  const titleGroupIds = _compact(items.reduce((acc, item) => {
    return acc.push(createTitleGroupId(item.get('contentId'), item.get('contentType')))
  }, List()).toJS()) || []

  const showNumbers = [
    DISPLAY_MODE_TILE_LIST_TOP_TEN,
    DISPLAY_MODE_TILE_LIST_TOP_TEN_3X4,
  ].includes(displayMode)

  const showStack = displayMode === DISPLAY_MODE_COURSES

  if (
    tabs.size === 0
    || !activeTab
    || titleGroupIds.length === 0
    || (isWinbackRow && !isWinbackFlowActive)
  ) {
    return null
  }

  if (removed) {
    return (
      <SectionRemoved
        sectionId={sectionId}
        sectionTitle={sectionTitle}
        addBackHandler={addBackHandler}
        adminTitle={adminTitle}
        staticText={staticText}
      />
    )
  }

  return (
    <div className={sectionClassName} data-page-manager-section-id={sectionId}>
      <div className="section-tiles-list__top-bg"></div>
      <div className="section-tiles-list__wrapper test-member-home-row">
        <div className="section-tiles-list__container">
          <SectionTilesListHeader
            sectionType={sectionType}
            displayMode={displayMode}
            sectionTitle={sectionTitle}
            footerLabel={footerLabel}
            defaultTabLabel={defaultTab ? defaultTab.get('tabLabel') : ''}
            onTabChange={handleTabChange}
            tabs={tabs}
            viewAll={viewAll}
            onViewAllClick={handleViewAllClick}
          />
          <div className={rowClassName}>
            <TileV2GraphqlManager
              {...props}
              onRenderContent={onRenderContent}
              onVisibilityChange={onVisibilityChange}
              titleGroupIds={titleGroupIds}
              adminTitle={adminTitle}
              upstreamContext={updateUpstreamContext(
                upstreamContext.current,
                CONTEXT_NAME_ADMIN_TITLE,
                adminTitle,
              )}
              language={language}
              display={tileDisplay}
              rowStyle={rowStyle}
              showNumbers={showNumbers}
              showStack={showStack}
              rowPlacement={rowPlacement}
            />
          </div>
        </div>
      </div>
    </div>
  )
}

export default compose(
  connectRedux(
    (state, props) => {
      const {
        page,
        pmSection,
        user,
        staticText,
        featureTracking,
        optimizelyFeature,
      } = state
      const { sectionId } = props
      const section = pmSection.get(sectionId) || Map()
      const tabs = getPmSectionTabs({ sectionId, pmSection })
        .filter((tab) => !tab.get('tabContent', List()).isEmpty())
      const defaultTab = tabs.get(0)
      // Get listType and data from the first tab (legacy support)
      const listType = defaultTab ? defaultTab.get('listType') : undefined
      const data = defaultTab ? Map({ listItems: defaultTab.get('tabContent', List()) }) : Map()

      // winback flow
      const isWinbackRow = pmSection.getIn([sectionId, 'data', 'adminTitle']) === WINBACK_ROW
      const isWinbackFlowActive = isWinbackFlagEnabled(optimizelyFeature)

      return {
        page,
        section,
        listType,
        tabs,
        defaultTab,
        data,
        featureTracking,
        hasData: section && Map.isMap(section) ? section.has('data') : false,
        processing: section && Map.isMap(section) ? section.get('processing', false) : false,
        language: getUserLanguagePrimary(user) || EN,
        adminTitle: section && Map.isMap(section) ? section.getIn(['data', 'adminTitle']) : undefined,
        displayMode: section && Map.isMap(section) ? section.getIn(['data', 'data', 'displayMode'], '') : '',
        headerLabel: section && Map.isMap(section) ? section.getIn(['data', 'data', 'header', 'label'], '') : '',
        footerLabel: section && Map.isMap(section) ? section.getIn(['data', 'data', 'footer', 'label'], '') : '',
        viewAllLink: section && Map.isMap(section) ? (section.getIn(['data', 'data', 'viewAllLink', 'web'], Map()) || Map()) : Map(),
        staticText: staticText.getIn(['data', 'sectionTilesList', 'data'], Map()),
        isWinbackFlowActive,
        isWinbackRow,
      }
    },
    (dispatch) => {
      const actions = getBoundActions(dispatch)
      return {
        setUpstreamContext: actions.upstreamContext.setUpstreamContext,
        setEventDefault: actions.eventTracking.setEventDefault,
      }
    },
  ),
)(SectionTilesList)
